import { useState } from 'react';
import { Card, CardBody, Typography, Chip } from '@material-tailwind/react';
import { ColorPicker } from '../../config/ColorPicker';
import simpletaskService from '../../service/simpletask.service';
import useParam from '../../context/ParamContext';
import { DialogUI, SelectUI } from '../Interface';
import { TaskReportForm } from '../TaskReport/TaskReportForm';
import ConfirmDialogUI from '../Interface/ConfirmDialogUI';
import { Box, IconButton, Button, Badge, Stack } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import MessageIcon from '@mui/icons-material/Message';
import AttachmentIcon from '@mui/icons-material/Attachment';
import NorthEastIcon from '@mui/icons-material/NorthEast';
import dayjs from 'dayjs';

export default function TodoCard({
  task,
  setMyTodo,
  user,
  announctedTaskStatus,
  setShowEmployeeAttendanceDialog,
  getTaskById,
  autoCompleteOptions,
  setCommentDialogData,
  setAttachmentDialog,
  getProjectById,
}) {
  const [showDialog, setShowDialog] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmDialogData, setConfirmDialogData] = useState(null);
  const { params } = useParam();

  const carriedStatus = params.MYTODO_CARRIED_STATUS;
  const finishedTaskStatus = params.MYTODO_FINISHED_TASK_STATUS ? parseInt(params.MYTODO_FINISHED_TASK_STATUS) : null;
  const taskReport = params.MYTODO_TASK_REPORT;
  const joinToTask = params.MYTODO_JOIN_TO_TASK ? parseInt(params.MYTODO_JOIN_TO_TASK) : null;
  const disabledStatus = params.DISABLED_MYTODO_STATUS_FIELD && params.DISABLED_MYTODO_STATUS_FIELD === 'Y';
  const taskStart = params.MYTODO_START_TASK_STATUS ? parseInt(params.MYTODO_START_TASK_STATUS) : null;

  const own = task.resposiblePersonId === user.userId;
  const iAmResponsibleFor = Boolean(task.simpleTaskRespPersonlist?.find((rp) => rp.userId === user.userId));
  const haveRespPreson = Boolean(task.resposiblePersonId);

  const handleTakeResponsibility = () => {
    if (task.responsiblePerson === null) {
      const updData = {
        simpleTaskId: task.simpleTaskId,
        resposiblePersonId: user.userId,
      };

      if (carriedStatus) {
        updData.statusId = parseInt(carriedStatus);
      }

      simpletaskService.updateRespPerson(updData).then((data) => {
        setMyTodo((prev) => {
          return prev.filter((task) => task.simpleTaskId !== data.simpleTaskId);
        });
      });
    } else {
      const data = {
        userId: user.userId,
        roleName: 'Közreműködő',
      };
      simpletaskService.addRespPersonToList(data, task.simpleTaskId).then((data) => {
        setMyTodo((prev) => {
          return prev.map((t) => {
            if (t.simpleTaskId === task.simpleTaskId) {
              return {
                ...t,
                simpleTaskRespPersonlist: data.respPersonlist,
              };
            }
            return t;
          });
        });
      });
    }
  };

  const handleStatusUpdate = (statusId) => {
    simpletaskService
      .updateStatus({ simpleTaskId: task.simpleTaskId, statusId, updatedBy: user.userId })
      .then((data) => {
        setMyTodo((prev) => {
          const index = prev.findIndex((t) => t.simpleTaskId === task.simpleTaskId);

          if (index === -1) return prev;

          if (data.status.final) {
            return prev.filter((_, i) => i !== index);
          }

          const newArray = [...prev];
          newArray[index] = data;

          return newArray;
        });
        if (showConfirmDialog) {
          setShowConfirmDialog(false);
        }
      })
      .finally(() => {
        //@TODO loader
      });
  };

  const openConfirmDialog = (statusId) => {
    setConfirmDialogData({
      statusId,
      action: () => {
        handleStatusUpdate(statusId);
        setShowConfirmDialog(false);
      },
    });
    setShowConfirmDialog(true);
  };

  const getFieldValue = (obj, path) => {
    return path.split('.').reduce((acc, part) => {
      if (Array.isArray(acc)) {
        return acc.map((item) => item[part]);
      } else {
        return acc ? acc[part] : null;
      }
    }, obj);
  };

  const getColor = (fieldColor) => {
    if (!fieldColor) return null;

    const color = getFieldValue(task, fieldColor);
    return ColorPicker.find((col) => col.colorName === color);
  };

  const cardBackgroundColor = getColor(params.MYTODO_CARD_BACKGROUND);
  const priorityColor = getColor('priority.color');
  const statusColor = getColor('status.color');

  const showOpenProjectButton = task.projectId && params.MYTODO_SHOW_OPEN_PROJECT_BUTTON === 'Y';

  return (
    <Card
      className="w-[450px]"
      style={{
        border: '1px solid #e6e6e6',
        borderTop: `7px solid ${statusColor?.colorCode}`,
        background: cardBackgroundColor?.colorBgCode,
      }}
    >
      <CardBody>
        <Typography variant="h5" color="blue-gray" className="items-center flex gap-6">
          {task.simpleTaskName}
        </Typography>
        <Typography>{task.taskType?.simpleTaskTypeName}</Typography>
        {task.project?.projectName && (
          <Typography>
            <Box component="span" fontWeight={700}>
              Projekt:{' '}
            </Box>
            {task.project.projectName}
          </Typography>
        )}
        {params.MYTODO_SHOW_TASK_ENDDATE === 'Y' && (
          <Typography>
            <Box component="span" fontWeight={700}>
              Határidő:{' '}
            </Box>
            {dayjs(task.endDate).format('YYYY-MM-DD')}
          </Typography>
        )}
        <Box display="flex" alignItems="center" gap="1rem" mt={1}>
          <Chip
            variant="ghost"
            value={task.priority?.itemName}
            className="rounded-full"
            style={{ background: priorityColor?.colorBgCode, color: priorityColor?.textColor }}
          />
          <SelectUI
            onChange={(e) => {
              handleStatusUpdate(e.target.value);
            }}
            value={task.statusId}
            option={autoCompleteOptions.status}
            disabled={disabledStatus}
            variant="outlined"
            size="small"
          />
          <Box>
            <IconButton
              onClick={() => getTaskById(task.simpleTaskId)}
              sx={{
                border: '1px solid lightgrey',
                mt: -0.2,
                borderRadius: '10px',
                '&:hover': {
                  backgroundColor: '#effaf3',
                  borderColor: '#BFE9D2',
                },
              }}
            >
              <svg width="25" height="24" viewBox="0 0 256 256" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M251 123.13C250.63 122.32 241.87 102.87 222.52 83.52C196.63 57.67 164 44 128 44C92 44 59.37 57.67 33.51 83.52C14.16 102.87 5.40002 122.32 5.00002 123.13C4.32255 124.666 3.97266 126.326 3.97266 128.005C3.97266 129.684 4.32255 131.344 5.00002 132.88C5.37002 133.7 14.13 153.14 33.49 172.49C59.37 198.34 92 212 128 212C164 212 196.63 198.34 222.48 172.49C241.84 153.14 250.6 133.7 250.97 132.88C251.652 131.346 252.007 129.687 252.012 128.008C252.018 126.329 251.673 124.668 251 123.13ZM204.94 156.13C183.47 177.27 157.59 188 128 188C98.41 188 72.53 177.27 51.09 156.12C42.6535 147.772 35.3964 138.312 29.52 128C35.3981 117.693 42.655 108.235 51.09 99.89C72.54 78.73 98.41 68 128 68C157.59 68 183.46 78.73 204.91 99.89C213.346 108.235 220.603 117.692 226.48 128C220.603 138.311 213.346 147.772 204.91 156.12L204.94 156.13ZM128 84C119.298 84 110.791 86.5806 103.555 91.4153C96.3192 96.2501 90.6796 103.122 87.3493 111.162C84.0191 119.202 83.1477 128.049 84.8455 136.584C86.5432 145.119 90.7338 152.959 96.8873 159.113C103.041 165.266 110.881 169.457 119.416 171.155C127.951 172.852 136.798 171.981 144.838 168.651C152.878 165.32 159.75 159.681 164.585 152.445C169.419 145.209 172 136.702 172 128C171.987 116.335 167.347 105.151 159.098 96.9019C150.849 88.6532 139.665 84.0132 128 84ZM128 148C124.044 148 120.178 146.827 116.889 144.629C113.6 142.432 111.036 139.308 109.522 135.654C108.009 131.999 107.613 127.978 108.384 124.098C109.156 120.219 111.061 116.655 113.858 113.858C116.655 111.061 120.219 109.156 124.098 108.384C127.978 107.613 131.999 108.009 135.654 109.522C139.308 111.036 142.432 113.6 144.629 116.889C146.827 120.178 148 124.044 148 128C148 133.304 145.893 138.391 142.142 142.142C138.391 145.893 133.304 148 128 148Z"
                  fill="#0fbd1d"
                />
              </svg>
            </IconButton>
          </Box>
        </Box>
        <div className="pt-5 flex gap-3 justify-end items-center">
          <Stack flexDirection="row" gap={2} mr={1} alignItems="center">
            <Badge
              badgeContent={task.commentCount || '0'}
              color="secondary"
              sx={{
                color: 'grey',
                mt: 1,
                cursor: 'pointer',
              }}
              onClick={() => {
                setCommentDialogData({
                  open: true,
                  lineId: task.simpleTaskId,
                  subMenuUrl: 'simpleTask',
                });
              }}
            >
              <MessageIcon fontSize="large" />
            </Badge>
            <Badge
              badgeContent={task.simpleTaskAttachment.length || '0'}
              color="secondary"
              sx={{
                color: 'grey',
                mt: 1,
                cursor: 'pointer',
              }}
              onClick={() => {
                setAttachmentDialog({
                  open: true,
                  attachments: task.simpleTaskAttachment,
                  simpleTaskId: task.simpleTaskId,
                });
              }}
            >
              <AttachmentIcon fontSize="large" />
            </Badge>

            {showOpenProjectButton && (
              <IconButton
                sx={{ border: '1px solid lightgrey', borderRadius: '10px', ml: 1 }}
                size="small"
                onClick={() => getProjectById(task.projectId)}
              >
                <Stack direction="row" alignItems="center" sx={{ color: '#0fbd1d' }}>
                  <Typography fontWeight="bold">P</Typography>
                  <NorthEastIcon />
                </Stack>
              </IconButton>
            )}
          </Stack>
          {!own &&
            !iAmResponsibleFor &&
            ((haveRespPreson && (joinToTask === null || joinToTask === task.statusId)) || !haveRespPreson ? (
              <Button
                variant="outlined"
                sx={{ color: '#0fbd1d', borderColor: '#0fbd1d' }}
                onClick={handleTakeResponsibility}
              >
                {haveRespPreson ? 'Csatlakozom' : 'Vittem'}
              </Button>
            ) : null)}
          {own &&
            taskReport === 'y' &&
            (params.MYTODO_USE_EMPLOYEE_ATTENDANCE_FOR_REPORT === 'Y' ? (
              <IconButton
                sx={{
                  backgroundColor: '#78b8ef',
                  borderRadius: '10px',
                  '&:hover': {
                    backgroundColor: '#5fa6dc',
                  },
                }}
                onClick={() =>
                  setShowEmployeeAttendanceDialog({
                    open: true,
                    data: {
                      projectId: task.projectId,
                      ...(params.SHOW_EMPLOYEE_ATTENDANCE_SIMPLETASK_FIELD === 'Y' && {
                        simpleTaskId: task.simpleTaskId,
                      }),
                    },
                  })
                }
              >
                <AccessTimeIcon sx={{ fontSize: { xs: '4rem', md: '2.3rem' }, color: 'white' }} />
              </IconButton>
            ) : (
              <Button
                variant="outlined"
                sx={{ color: '#0fbd1d', borderColor: '#0fbd1d' }}
                onClick={() => setShowDialog(true)}
              >
                Lejelentés
              </Button>
            ))}
          {task.statusId !== announctedTaskStatus &&
            task.statusId !== finishedTaskStatus &&
            task.statusId < finishedTaskStatus &&
            finishedTaskStatus &&
            own && (
              <Button
                variant="outlined"
                sx={{ color: '#0fbd1d', borderColor: '#0fbd1d' }}
                onClick={() => openConfirmDialog(finishedTaskStatus)}
              >
                Befejeztem
              </Button>
            )}
          {task.statusId === announctedTaskStatus && own && (
            <Button
              variant="outlined"
              sx={{ color: '#0fbd1d', borderColor: '#0fbd1d' }}
              onClick={() => openConfirmDialog(taskStart)}
            >
              Elkezdem
            </Button>
          )}
        </div>
      </CardBody>

      <ConfirmDialogUI
        handleChangeElement={confirmDialogData?.action}
        show={showConfirmDialog}
        onHide={() => setShowConfirmDialog(false)}
      />

      <DialogUI
        open={showDialog}
        headerContent={'Feladat lejelentő'}
        fullWidth={true}
        onClose={() => setShowDialog(false)}
      >
        <TaskReportForm showDialog={showDialog} selectedTask={task} setShowDialog={setShowDialog} />
      </DialogUI>
    </Card>
  );
}
